import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccountData } from "../../../account/src/store/account/action";
import { fetchCandidateData } from "../../../candidate/src/store/candidate/action";

import { fetchJobData } from "../../../job/src/store/job/action";
import { fetchTosByJobIdAndCandidateId } from "@workspace/job/src/store/actions";

import { extractNameAndEmail } from "./TemplateModuleDataUtil";
import { useUserAuth } from "../../../login/src/hooks/UserAuthHook";

export const useTemplateModuleData = (dataId) => {
  const dispatch = useDispatch();
  const accountData = useSelector((state) => state.AccountReducer.accountData);
  const candidateData = useSelector(
    (state) => state.CandidateReducer.candidateData
  );
  const tosAllData = useSelector((state) => state.TosReducer.tos);
  const jobData = useSelector((state) => state.JobReducer.jobData);
  const { userProfile } = useUserAuth();
  const [accountId, setAccountId] = useState(dataId?.accountId || null);
  const [jobId, setJobId] = useState(dataId?.jobId || null);
  const [candidateId, setCandidateId] = useState(dataId?.candidateId || null);
  const [allIds, setAllIds] = useState({
    accountId: dataId?.accountId || null,
    jobId: dataId?.jobId || null,
    candidateId: dataId?.candidateId || null,
  });
  const [allModuleData, setAllModuleData] = useState({});
  const [isAllLoading, setIsAllLoading] = useState(true);

  const setAllIdsHandler = () => {
    setAllIds({
      accountId: accountId,
      jobId: jobId,
      candidateId: candidateId,
    });
  };

  useEffect(() => {
    setAllModuleData((prevData) => {
      const obj = { ...prevData };

      // Set user profile information
      if (userProfile) {
        obj["User"] = { basicInfo: userProfile };
      }

      // Set account data
      if (accountData) {
        obj["Accounts"] = accountData;
      }

      // Add TOS information if available
      if (tosAllData) {
        obj["JobTimeline"] = { tosForm: tosAllData };
        obj["TOS"] = { tosInfo: tosAllData?.tosSubmissionData }; // Ensure TOS data is added to the object
      }

      // Set candidate data and TOS data
      if (candidateData) {
        obj["Candidates"] = candidateData;

        // Extract candidate owner name and email
        const candidateOwnerOriginal = candidateData.basicInfo?.candidateOwner;
        if (candidateOwnerOriginal) {
          const nameEmail = extractNameAndEmail(candidateOwnerOriginal);
          if (
            candidateData.basicInfo?.candidateOwner &&
            nameEmail?.email &&
            nameEmail?.name
          ) {
            obj["Candidates"]["basicInfo"]["candidateOwner"] = nameEmail.name;
            obj["Candidates"]["basicInfo"]["candidateOwnerEmail"] =
              nameEmail?.email;
          }
        }
      }

      // Set job data and extract account owner information
      if (jobData) {
        obj["Jobs"] = jobData;

        const accountOwnerOriginal = jobData?.jobInfo?.accountOwner;
        if (accountOwnerOriginal) {
          const nameEmail = extractNameAndEmail(accountOwnerOriginal);
          if (
            jobData?.jobInfo?.accountOwner &&
            nameEmail?.email &&
            nameEmail?.name
          ) {
            obj["Jobs"]["jobInfo"]["accountOwner"] = nameEmail.name;
            obj["Jobs"]["jobInfo"]["accountOwnerEmail"] = nameEmail?.email;
          }
        }
      }

      return obj;
    });
  }, [accountData, candidateData, jobData, tosAllData]);

  useEffect(() => {
    const getData = async () => {
      if (accountId || jobId || candidateId) {
        try {
          setIsAllLoading(true);
          await Promise.all([
            accountId && dispatch(fetchAccountData(accountId)),
            jobId && dispatch(fetchJobData(jobId)),
            candidateId && dispatch(fetchCandidateData(candidateId)),
            jobId &&
              candidateId &&
              dispatch(
                fetchTosByJobIdAndCandidateId({
                  jobId: jobId,
                  candidateId: candidateId,
                  status: "SUBMITTED",
                })
              ),
          ]);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsAllLoading(false);
        }
      }
    };

    getData();
  }, [accountId, jobId, candidateId]);

  return {
    allModuleData,
    setAccountId,
    setJobId,
    setCandidateId,
    setAllIdsHandler,
    isAllLoading,
    setAllModuleData,
  };
};
